/**
 * Credit-lot expiry day convention (DNKADM-0306, on the location calendar since
 * the DNKADM-0439 Operator ruling) — the single place this convention is expressed.
 *
 * A lot's `expiresAt` is a DAY typed as an instant, and dink-api's usability
 * predicate is strictly EXCLUSIVE (`expiresAt > :now`, `V2CreditLotRepository`)
 * — so the instant is the first DEAD moment, not the last good one. dink-api
 * writes it at the tenant's local midnight after the last usable day
 * (DNKAPI-0604/0605, `MembershipLotExpiry.afterLastDay`/`startOfDay`, kept by
 * DNKAPI-0642's `TenantCalendar.endOfLocalDay`): a New York lot good through
 * Oct 16 carries `2026-10-17T04:00:00Z`, an Auckland one `2026-10-16T11:00:00Z`.
 *
 * DNKAPI-0648 (DNKADM-0442 U3): when the API sends the lot's `lastUsableDay` (a plain
 * tenant-calendar `YYYY-MM-DD`) it is displayed AS-IS, never converted through any zone,
 * and staff writes send the picked day as a plain date. The instant paths below stay for an
 * older API that does not send the field.
 *
 * Everything here states expiry as that LAST USABLE DAY ("Good through …" — the
 * wording is load-bearing: it is what makes the date unambiguous to an operator
 * reading it to a customer), on the calendar `zone` (the location's, then the
 * tenant's, then the device's: `useCalendarZone`, DNKADM-0439):
 *  - read: the `zone` day of the last usable millisecond (`expiresAt - 1 ms`).
 *    For a boundary at local midnight that is the day before; for the old
 *    `T00:00:00Z` shape west of UTC it is the same day the UTC rule gave. Do NOT
 *    take the UTC date — east of UTC it reads one day early.
 *  - write: the start of (picked day + 1) in `zone`, DST-correct. dink-api keeps
 *    an instant that is not exactly `T00:00:00Z` as sent
 *    (`MembershipLotExpiry.fromUtcMidnight`), so this is the boundary stored.
 *    With no zone the write keeps the legacy `T00:00:00Z` shape, which dink-api
 *    normalizes to tenant midnight (never the device's midnight).
 */

import { addDays, dayStartIso, zonedDateOf } from '@/components/list/listDate';
import { shortDateLabel } from '@/utils/clockTime';

/**
 * The `YYYY-MM-DD` of a lot's last usable day in `zone` (`null`: the device):
 * the day of the last millisecond before its exclusive `expiresAt` boundary.
 * Non-finite input returns the raw string unchanged (degrade, never throw).
 */
export function lastUsableDayKey(expiresAt: string, zone: string | null): string {
  const ms = Date.parse(expiresAt);
  if (!Number.isFinite(ms)) return expiresAt;
  return zonedDateOf(ms - 1, zone);
}

/**
 * "Good through {Mon D, YYYY}" (the API's `lastUsableDay` as-is when sent, else the last usable day in `zone`) / `null` →
 * "Never expires" / non-finite → "Expires {raw}" — never throws.
 */
export function expiryLabel(
  expiresAt: string | null,
  zone: string | null,
  lastUsableDay: string | null = null,
): string {
  if (lastUsableDay !== null) return `Good through ${shortDateLabel(lastUsableDay) ?? lastUsableDay}`;
  if (expiresAt === null) return 'Never expires';
  if (!Number.isFinite(Date.parse(expiresAt))) return `Expires ${expiresAt}`;
  const key = lastUsableDayKey(expiresAt, zone);
  return `Good through ${shortDateLabel(key) ?? key}`;
}

/**
 * The exclusive boundary instant for a picked last-usable day: the start of
 * `dayKey` + 1 in `zone`, ISO `Z`. The exact inverse of `lastUsableDayKey` in the
 * same zone. With no zone (`null`: no location loaded) it is NOT the device's
 * midnight, which dink-api would store as sent and which can end a paid lot
 * early on a device east of the venue: it keeps the legacy wire, picked + 1 at
 * `T00:00:00Z`, which dink-api moves to the tenant's local midnight
 * (`MembershipLotExpiry.fromUtcMidnight`). A value that is not `YYYY-MM-DD`
 * returns unchanged (degrade, never throw — the pickers validate upstream).
 */
export function boundaryInstantForLastUsableDay(dayKey: string, zone: string | null): string {
  return dayStartIso(addDays(dayKey, 1), zone ?? 'UTC') ?? dayKey;
}

/**
 * A lot's last usable day as a `YYYY-MM-DD`: the API's own `lastUsableDay` when sent
 * (as-is), else derived from the instant in `zone`; `null` when it never expires or the
 * instant is unparseable. Used for pre-fills and "same day" comparisons.
 */
export function lastUsableDayOf(
  expiresAt: string | null,
  zone: string | null,
  lastUsableDay: string | null,
): string | null {
  if (lastUsableDay !== null) return lastUsableDay;
  if (expiresAt === null || !Number.isFinite(Date.parse(expiresAt))) return null;
  return lastUsableDayKey(expiresAt, zone);
}
